import Layout from '@/components/Layout';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { useEffect, useState } from 'react';
import { AiOutlineReload } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Monitors = () => {
    const [loading, setLoading] = useState(true);
    const [monitors, setMonitors] = useState([]);
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();

    const fetchMonitors = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/monitors');
            setMonitors(response.data);
            console.log('response.data', JSON.stringify(response.data, null, 2));
        } catch (error) {
            console.error('Error fetching monitors:', error);
            toast.error('Error fetching monitors, please try again later');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMonitors();
    }, []);

    return (
        <Layout>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
            >
                <Card>
                    <CardHeader className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between">
                        <CardTitle className="text-3xl font-bold">Monitors</CardTitle>
                        <div className="flex space-x-4">
                            <Button variant="ghost" size="icon" onClick={() => fetchMonitors()}>
                                <AiOutlineReload className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button onClick={() => navigate('/monitors/new')}>
                                Add Monitor
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Uptime</TableHead>
                                        <TableHead>Response Time</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {[...Array(5)].map((_, i) => (
                                        <TableRow key={i}>
                                            {[...Array(4)].map((_, i) => (
                                                <TableCell key={i}>
                                                    <Skeleton className="h-8 w-full" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : monitors.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Organization</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Action Type</TableHead>
                                        <TableHead>Interval</TableHead>
                                        <TableHead>Last Run</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {monitors.map((monitor) => (
                                        <TableRow key={monitor.formatted.id} className="cursor-pointer" onClick={() => navigate(`/monitors/${monitor.formatted.id}`)}>
                                            <TableCell>{monitor.formatted.name}</TableCell>
                                            <TableCell>{monitor.formatted.organizationName}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <div className="relative">
                                                        <div className={`w-2.5 h-2.5 rounded-full ${
                                                            monitor.formatted.status.toLowerCase() === 'running' ? 'bg-emerald-400' : 'bg-yellow-500'
                                                        }`}></div>
                                                        {monitor.formatted.status.toLowerCase() === 'running' && (
                                                            <div className="absolute inset-0 w-2.5 h-2.5 bg-emerald-400 rounded-full animate-ping" />
                                                        )}
                                                    </div>
                                                    <span className="ml-2">
                                                        {monitor.formatted.status}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>{monitor.formatted.actionType}</TableCell>
                                            <TableCell>{monitor.formatted.interval}</TableCell>
                                            <TableCell>
                                                {monitor.formatted.lastRun ? (
                                                    <>
                                                        <span className={
                                                            monitor.formatted.lastRun.success ? 'text-emerald-400' : 'text-red-400'
                                                        }>
                                                            {monitor.formatted.lastRun.success ? 'Success' : 'Failed'}
                                                        </span>
                                                        {monitor.formatted.lastRun.responseTime && ` (${monitor.formatted.lastRun.responseTime})`}
                                                        <div className="text-xs text-gray-500">{monitor.formatted.lastRun.startTime}</div>
                                                        {monitor.formatted.lastRun.message && (
                                                            <div className="text-sm text-gray-600 mt-1">{monitor.formatted.lastRun.message}</div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-gray-500">No runs yet</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-lg font-semibold">No monitors found</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </motion.div>
        </Layout>
    );
};

export default Monitors;
